import { useMemo } from "react";
import type { ApexOptions } from "apexcharts";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { DiscountedBookingsChartProps } from "./DiscountedBookingsChart";
import { CHART_DATE_TICK_AMOUNT, formatChartDateAxisLabel } from "../../../utils/chart";

export const useDiscountedBookingsChartController = ({ metric }: DiscountedBookingsChartProps) => {
  const i18n = useI18n();
  const totalBookings = Math.round(metric?.total?.value ?? 0);

  const categories = useMemo(
    () => metric?.timeSeries.dataPoints.map((dp) => new Date(dp.time).getTime()) || [],
    [metric?.timeSeries.dataPoints]
  );

  const series: ApexAxisChartSeries = useMemo(
    () => [
      {
        name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_data_key")),
        data: metric?.timeSeries.dataPoints.map((dp) => dp.value) || [],
      },
    ],
    [metric?.timeSeries.dataPoints, i18n]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "straight",
        width: 2,
      },
      colors: ["var(--bui_color_action_foreground)"],
      markers: {
        size: 5,
        colors: ["var(--bui_color_action_foreground)"],
        strokeColors: "var(--bui_color_on_action_background)",
        strokeWidth: 2,
        hover: { size: 7 },
      },
      xaxis: {
        type: "category",
        categories,
        tickPlacement: "on",
        tickAmount: CHART_DATE_TICK_AMOUNT,
        title: {
          text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_xaxis")),
          offsetY: 24,
        },
        labels: {
          rotate: 0,
          rotateAlways: false,
          formatter: formatChartDateAxisLabel,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          formatter: (val: number) => Math.round(val).toString(),
          padding: 60,
        },
      },
      grid: {
        borderColor: "var(--bui_color_border_alt)",
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 24, left: 80, right: 40, bottom: 20 },
      },
      tooltip: {
        intersect: true,
        shared: false,
        x: { format: "d MMM yyyy" },
        y: {
          title: {
            formatter: () => `${i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_yaxis"))}:`,
          },
          formatter: (val: number) => Math.round(val).toString(),
        },
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "top",
        horizontalAlign: "right",
        markers: { size: 7, shape: "circle" },
        offsetY: 0,
        onItemClick: { toggleDataSeries: false },
      },
    }),
    [categories, i18n]
  );

  return {
    totalBookings,
    options,
    series,
  };
};
